import React, { memo, useEffect } from "react";
import {
  Box,
  Group,
  Paper,
  Header as MHeader,
  Burger,
  Anchor,
  Menu,
  Space,
  Button,
  useMantineTheme,
  ActionIcon,
  MenuProps,
} from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { ThemeSwitch } from "./Components/ThemeSwitch";
import GlobalSearch from "./Components/GlobalSearch";
import { useGlobalStatusContext } from "./GlobalStore";
import { useIsSmallerThanMD, useIsSmallScreen } from "./Components/Utils";
import { AiOutlineSearch } from "react-icons/ai";
import { ChainSwitch } from "./Components/ChainSwitch";
import Icon from "./Components/Icon";
import { ROUTER_BASE, Link } from "./Components/Link";
import Logo from "./ArbitrageScanSkeleton/Logo";

export const BountiesButton = memo(
  ({
    style,
    hideIcon,
    trigger = "hover",
    onClick,
  }: {
    style?: React.CSSProperties;
    hideIcon?: boolean;
    trigger?: MenuProps["trigger"];
    onClick: (href: string) => void;
  }) => {
    return (
      <Menu trigger={trigger} position="bottom-end">
        <Menu.Target>
          <Button
            size="sm"
            variant="subtle"
            sx={{
              fontSize: 14,
              fontWeight: 400,
              background: "linear-gradient(270deg,#148ee0, #00b591)",
              backgroundClip: "text",
              color: "transparent",
              "&:hover": {
                background: "rgba(68,215,182,0.10) !important",
                backgroundClip: "unset",
                color: "#44D7B6",
              },
              ...style,
            }}
          >
            <Group noWrap spacing={6}>
              {hideIcon ? null : (
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                    background: "linear-gradient(270deg,#148ee0, #00b591)",
                    backgroundClip: "text",
                    color: "rgba(255, 255, 255, 0)",
                  }}
                >
                  <Icon name="icon-bounties" size={14} />
                </Box>
              )}
              <Box
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  background: "linear-gradient(270deg,#148ee0, #00b591)",
                  backgroundClip: "text",
                  color: "rgba(255, 255, 255, 0)",
                }}
              >
                Bounties
              </Box>
            </Group>
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            onClick={() => onClick(`/${ROUTER_BASE}/bounty/BugImprovement`)}
          >
            Bug and Improvement
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  }
);

export default function Header({ isExtend }: { isExtend: boolean }) {
  const isSmallScreen = useIsSmallScreen();
  const isSmallerThanMD = useIsSmallerThanMD();

  const { ref } = useElementSize();
  const { setNavExpand, chain } = useGlobalStatusContext();
  const { navVisibleOnSmallScreen, setNavVisibleOnSmallScreen } =
    useGlobalStatusContext();
  const theme = useMantineTheme();

  // 修复移动端滚动穿透的问题
  useEffect(() => {
    if (isSmallScreen) {
      if (navVisibleOnSmallScreen) {
        let scrollTop =
          document.body.scrollTop || document.documentElement.scrollTop;
        document.body.style.cssText +=
          "overflow:hidden;position:fixed;width:100%;top:-" + scrollTop + "px;";
      } else {
        let body = document.body;
        body.style.position = "";
        let top = body.style.top;
        document.body.scrollTop = document.documentElement.scrollTop =
          -parseInt(top);
        body.style.top = "";
        body.style.cssText = "overflow:auto";
      }
    }
  }, [isSmallScreen, navVisibleOnSmallScreen]);

  const renderRightContent = () => {
    if (isSmallScreen) {
      return (
        <Group spacing={2}>
          <Anchor
            size={"sm"}
            component={Link}
            to={`/${chain}/search`}
            sx={{ lineHeight: 1 }}
          >
            <AiOutlineSearch size="22" />
          </Anchor>
          <Burger
            size={"sm"}
            opened={navVisibleOnSmallScreen}
            onClick={() => {
              setNavVisibleOnSmallScreen(!navVisibleOnSmallScreen);
              setNavExpand(true);
            }}
          />
        </Group>
      );
    }

    const handleClick = (href: string) => {
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.href = href;
      a.target = "_blank";
      a.rel = "noreferrer";
      a.click();
      a.remove();
    };

    return (
      <Group spacing={0} noWrap>
        {/* <BountiesButton
          trigger="hover"
          hideIcon
          style={{
            fontWeight: 700,
          }}
          onClick={handleClick}
        /> */}
        <Button
          size="sm"
          variant="subtle"
          sx={{
            fontSize: 14,
            fontWeight: 400,
            color: theme.colorScheme === "dark" ? "#FFF" : "#25262B",
            "&:hover": {
              backgroundColor: "rgba(68,215,182,0.10) !important",
              color: "#44D7B6",
            },
          }}
          onClick={() => handleClick(`/${ROUTER_BASE}/${chain}/dailyReport`)}
        >
          Reports
        </Button>
        <Menu trigger="hover" position="bottom-end">
          <Menu.Target>
            <Button
              size="sm"
              variant="subtle"
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: theme.colorScheme === "dark" ? "#FFF" : "#25262B",
                "&:hover": {
                  backgroundColor: "rgba(68,215,182,0.10) !important",
                  color: "#44D7B6",
                },
              }}
            >
              Docs
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              icon={<Icon name="icon-Classroom" size={14} />}
              onClick={() =>
                handleClick(`https://eigenphi-1.gitbook.io/classroom/`)
              }
            >
              Classroom
            </Menu.Item>
            <Menu.Item
              icon={<Icon name="icon-Newsletter" size={14} />}
              onClick={() => handleClick(`https://eigenphi.substack.com/`)}
            >
              Newsletter
            </Menu.Item>
            <Menu.Item
              icon={<Icon name="icon-UserGuide" size={14} />}
              onClick={() =>
                handleClick(
                  `https://eigenphi-1.gitbook.io/arbitrage-scan-user-guide/`
                )
              }
            >
              User Guide
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        <ThemeSwitch />
        <Space />
      </Group>
    );
  };

  return (
    <MHeader height={80} fixed ref={ref} zIndex={201}>
      <Paper
        shadow="sm"
        component="nav"
        withBorder={false}
        px="md"
        py="md"
        sx={{ borderRadius: 0, border: 0 }}
      >
        <Group position="apart" noWrap sx={{ gap: 10, height: 46 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignContent: "center",
              alignItems: "center",
              flexWrap: "nowrap",
              flex: isSmallScreen ? "" : "0 1 476px",
            }}
          >
            <Group spacing={0} noWrap>
              <Box
                component={Link}
                to="/"
                onClick={() => {
                  document.getElementById("top")?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "center",
                  });
                }}
              >
                <Logo
                  dark={theme.colorScheme === "dark"}
                  isSmallScreen={isSmallScreen}
                />
              </Box>
              {/**
                 *
                 *
              <Logo
                style={{
                  textDecoration: "none",
                }}
                component={Link}
                to="/"
                onClick={() => {
                  document.getElementById("top")?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "center",
                  });
                }}
                size={32}
              />
                 */}
              <Space w={isSmallScreen ? "sm" : isSmallerThanMD ? 0 : 60} />
              {isSmallScreen ? null : (
                <ActionIcon
                  sx={(theme) => ({
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? "#25262B"
                        : "rgba(233,236,239,0.50)",
                    color: "#54B498",
                    transform: isExtend ? "" : "rotateY(180deg)",
                    "&:hover": {
                      color: "#44D7B6",
                      backgroundColor: "rgba(68,215,182,0.10)",
                    },
                  })}
                  onClick={() => {
                    setNavExpand(!isExtend);
                  }}
                >
                  <Icon name="icon-double-arrow-left" size={12} />
                </ActionIcon>
              )}
            </Group>
            <ChainSwitch type="tab" />
          </Box>
          {isSmallScreen ? null : <GlobalSearch />}
          {renderRightContent()}
        </Group>
      </Paper>
    </MHeader>
  );
}
